// AppGenFAQ.jsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const faqData = [
  {
    question: "Q: What types of filings can I generate using AppGen?",
    answer: `A: AppGen supports U.S. Continuation, Divisional, CIP, Bypass Continuation, and 371 National Stage filings.`,
  },
  {
    question: "Q: What information is required to create a filing package in AppGen?",
    answer: `A: You need the Parent Application Number, Filing Type, and Docket Number for generating the filing package.`,
  },
  {
    question: "Q: Does the parent application need to be published to create a filing package ?",
    answer: `A: Yes. AppGen can only generate a filing package if the parent application is published and publicly available in the USPTO or WIPO.`,
  },
  {
    question: "Q: Can I create multiple filings at once using AppGen?",
    answer: `A: No. AppGen supports creation of only one application per request.`,
  },
  {
    question: "Q: What USPTO forms are generated for Continuation, Divisional, and CIP filings?",
    answer: `A: Appgen generates:

            • AIA-14 – Application Data Sheet
            • SB-15A & SB-15B – Micro Entity Certification (Gross Income Basis) –  {If the application is Micro Entity}
            • AIA-01 – Declaration
            • AIA-82 – Power of Attorney Transmittal
            • SB-439 – Internet Communications Authorization
            • AIA-15 – Non-Provisional Patent Transmittal
            • SB-35 – Request Not to Publish (included only if selected in ADS)
            • Initial IDS – {Included only if the IDS option is selected in the UI}`,
  },
  {
    question: "Q: What forms are generated in a 371 filing package?",
    answer: `A: The system includes the following USPTO forms in the 371 filing package:
            • AIA-14 – Application Data Sheet
            • SB-15A & SB-15B – Micro Entity Certification (Gross Income Basis) - {Included only if the application is marked as Micro Entity}
            • AIA-01 – Declaration
            • AIA-82 – Power of Attorney Transmittal
            • SB-439 – Internet Communications Authorization
            • PTO-1390 – Transmittal for 371 National Stage Entry
            • SB-35 – Request Not to Publish (included only if selected in ADS)
            • Preliminary Amendment
            • Initial IDS – {Included only if the IDS option is selected in the UI}`,
  },
  {
    question: "Q: What PCT documents are included in the 371 filing package ?",
    answer: `A: If available, the following PCT documents are included in the final 371 filing package:

            • RO/101 – Request Form
            • Publication
            • International Search Report (ISR)
            • Written Opinion`,
  },
  {
    question: "Q: Can I include the attorney’s signature on the USPTO forms?",
    answer: (
      <>
        <Typography sx={{ whiteSpace: "pre-line", mb: 2 }}>
          A: Yes. In the AppGen UI, there is an option to check or uncheck whether you want the attorney’s signature to be included on the USPTO forms.
          <br></br>
          •  If checked, the system will apply the attorney signature to the applicable forms.
          <br></br>
          •  If unchecked, the forms will be generated without the signature, allowing for manual signing later if needed.
        </Typography>
        <img
          src="/images/june/faq/appgen_faq_img1.png"
          alt="FAQ Screenshot Q9"
          style={{ marginTop: 12 }}
        />
      </>
    ),
  },
  {
    question: "Q: How can I select or deselect Initial IDS generation in the AppGen UI?",
    answer: (
      <>
        A: During AppGen request submission, the user is provided with an option to select or deselect Initial IDS generation.
        <br /><br />
        • When the IDS option is selected, the system generates an Initial IDS as part of the filing package, provided the parent application is published and the required reference documents (SB08/1449, SB/892, or ISR) are available.
        <br />
        • When the IDS option is not selected, the Initial IDS is not generated, even if the reference documents are available.
        <br /><br />
        
          Note: 
          <br />
          <em>The IDS option is enabled by default and applies only to filing types that support Initial IDS generation.
          Processing time for generating the IDS may increase depending on the number of reference documents and the total number of pages included.
        </em>
      </>
    ),
  },
  {
    question: "Q: Do I need to fill in all the details on the ADS page in the UI?",
    answer: `A: No. system auto-selects common fields. User may update manually if needed, but ADS will still generate even with defaults.`,
  },
  {
    question: "Q: What happens if “Request not to publish” is selected in the ADS?",
    answer: `A: If selected, Form SB-35 will be automatically included in the filing package.`,
  },
  {
    question: "Q: What information is automatically filled in the Application Data Sheet (ADS)?",
    answer: (
      <>
        A: It auto-fills inventor data, applicant details, and priority information based on USPTO public data and parent application records.
        <br /><br />
        <em>
          Note: Corrected ADS from the parent application is not referred to or used when generating the new ADS.
        </em>
      </>
    ),
  },
  {
    question: "Q: How is the Declaration handled?",
    answer: `A: 
                • For Continuation and Divisional filings, the Declaration is pulled from the parent record. If the parent record has no Declaration, an unexecuted AIA-01 Declaration is generated for each inventor.
                • For 371 Filing and Continuation in part, unexecuted AIA-01 Declaration is generated for each inventor.`,
  },
  {
    question: "Q: How does AppGen handle page counts in the AIA-15 Transmittal?",
    answer: `A: AppGen currently does not update the number of Specification or Drawing pages in the AIA-15 Transmittal due to system limitations. The page-count fields in Form AIA-15 remain unchanged in the generated output.`,
  },
  {
    question: "Q: How is Form PTO-1390 handled if a Specification file is uploaded?",
    answer: `A: When a Specification file is uploaded, the system provides additional support in the PTO-1390 (Transmittal) form as follows:

            • The total number of claims and the number of independent claims are calculated from the Specification and populated in the Fee Calculation section.
            • The fee calculation is updated accordingly based on these counts.
            
            Note: The total number of sheets is not included due to a current system limitation in page number detection.`,
  },
  {
    question: "Q: What if I upload a GPOA?",
    answer: `A: If a GPOA (.pdf) is uploaded, AppGen automatically merges it with the AIA-82 (Power of Attorney Transmittal) form to create a single, ready-to-file POA document.`,
  },
  {
    question: "Q: What naming convention should be followed for Specification and Drawing files?",
    answer: (
      <>
        A: No specific naming convention is required for Specification and Drawing files.
        <br /><br />
        In the AppGen UI, there are dedicated upload sections for each document type (Specification and Drawings). Users must upload the documents in their respective sections, and the system will automatically identify and process them based on where they are uploaded.
        <br /><br />
        <em>
          Note:
          <br />
          • Ensure the correct document is uploaded in the corresponding section (Specification or Drawings).
          <br />
          • Files uploaded in the wrong section may not be processed as intended.
        </em>
      </>
    ),
  },
  {
    question: "Q: Are Specification, Drawing, and GPOA files mandatory files for Application package preparation?",
    answer: (
      <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
        A: No, these files are optional. If uploaded, the system performs validation as follows:
        <br />
        <strong>Specification (.docx):</strong><br />
        • Title must match the USPTO record<br />
        • Paragraph numbering must be sequential<br />
        • Claims must:<br />
          &nbsp;&nbsp;&nbsp;- Start from Claim 1<br />
          &nbsp;&nbsp;&nbsp;- Not depend on a later or non-existent claim <br />
        • Abstract must: <br />
          &nbsp;&nbsp;&nbsp;- Be present <br />
          &nbsp;&nbsp;&nbsp;- Be within the 150 words <br />
        • Cross-reference section (Continuation and Divisional Filing package)  <br />
          &nbsp;&nbsp;&nbsp;- If present, it is updated with the parent application details <br />
          &nbsp;&nbsp;&nbsp;- If no cross-reference section is found, the system does not add one. <br />

        <strong>Drawing (.docx):</strong> <br />
        • Figure numbers must be in sequence <br />
        • Validations (if uploaded with Specification): <br />
          &nbsp;&nbsp;&nbsp;- All figures mentioned in the Brief Description of Drawings must be present in the drawing file <br />
          &nbsp;&nbsp;&nbsp;- All figure numbers in the drawing file must be referenced in the Specification <br />
        Note: The system only flags issues (included in the email notification). It does not modify the uploaded files. <br />

        <strong>GPOA (.pdf):</strong> <br />
        • If uploaded, it is merged with AIA-82 to generate a single POA document <br />

        In the final package, the Docket Number is added to the header of the Specification and Drawing files.
        <br />
        • Complete Specification with Amendments(.docx) (Optional – 371 filings only) <br />
          This upload option is available only when a 371 National Stage filing package is selected.<br />
          &nbsp;&nbsp;&nbsp;•If provided, it will be used to identify amendments and update the Preliminary Amendment<br />
          &nbsp;&nbsp;&nbsp;• If not provided, the Preliminary Amendment will be generated using Cross-Reference section updates only.
      </Typography>
    )
  },
  {
    question: "Q: How are Specification and Drawing-related observations reported?",
    answer: (
      <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
        A: All validation observations for Specification and Drawing files are included in the email notification sent with the completed application package.
        
        <br />
        • If there are less than 6 observations, they appear directly in the email body under “Observations:”
        <br />
        • If there are 6 or more, the observations are provided in an attached Excel file.
      </Typography>
    ),
  },
  {
    question: "Q: When is the Cross-Reference section updated in the Specification?",
    answer: `A: The Cross-Reference section is updated only when:

            • The application type is either Continuation or Divisional, and
            • A Cross-Reference section already exists in the uploaded Specification file

            If both conditions are met, the system will amend the existing paragraph to include the current application relationship.
              • If the Cross-Reference section is missing, the system doesn’t create a new one.
              • System doesn’t verify the family relationship stated in the cross reference paragraph.`,
  },
  {
    question: "Q: Can 371 packages be created for PCT Application filed in non-English ?",
    answer: `A: Yes, AppGen will create the package for all PCT applications, irrespective of the receiving office, provided the application is published and publicly available.`,
  },
  {
    question: "Q: When does the system report an error during filing package creation?",
    answer: (
      <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
          A: The system reports an error and stops the processing in the following case:
          <br /><br />
          • <strong>Parent Application Not Found</strong>
          <br />
          &nbsp;&nbsp;&nbsp;If the entered parent application number is not published or not found in the USPTO or WIPO public database, the system will display an error and the filing package cannot be created.
      </Typography>)
  },
  {
    question: "Q: How is the output delivered after submitting a filing package request?",
    answer: (
        <Typography sx={{ whiteSpace: "pre-line", mb: 2 }}>
          A: Delivery depends on how long the request takes to process:
          <br /><br />
          • If the request takes more than 30 seconds:
          <br />
          - JUNE displays a submission confirmation.
          <br />
          - Once the request is completed, an email is sent with the download link.
          <br />
          • If the request is processed instantly (under 30 seconds):
          <br />
          - A successful message appears with a direct download link.
          <br />
          - An email with the same link is also sent.
        </Typography>

    ),
  },
  {
    question: "Q: For what filing types is the Initial IDS prepared?",
    answer: `A: Continuation, Continuation-in-part, Divisional, By-pass and 371 Application filing type`
  },
  {
    question: "Q: What references are included in the Initial IDS",
    answer: (
      <>
        A: The references mentioned in the SB08/1449 AND 892 documents for a US parent application, and the references mentioned in the ISR document of the parent PCT application for 371 filing type.
        <br /><br />
        <em>
          Note:
          <br />
          Processing time for generating the IDS may increase depending on the number of reference documents and the total number of pages included.
        </em>
      </>
    )
  },
  {
    question: "Q: Does the initial IDS do cross-referencing?",
    answer: `A: No, the IDS prepared as part of the AppGen filing package does not include cross-referencing. Only the references cited in SB08/1449 and 892 (for US parent) and ISR (for PCT parent). Their family members are NOT included`
  },
  {
    question: "Q: Is IDS always prepared as part of the AppGen filing package?",
    answer: `A: If the parent application is published and SB08/1449, 892, or ISR document(s) are found, the IDS will be prepared by JUNE by default. However, the user can also choose not to have IDS created by de-selecting the IDS checkbox during AppGen request form submission.`
  },
  {
    question: "Q: What input needs to be provided for the IDS to be created in JUNE?",
    answer: `A: Only the parent Application number through the AppGen request form (provided the parent is a published application)`
  },
  {
    question: "Q: For what filing types is the Preliminary Amendment prepared?",
    answer: `A:371 Application filing type`
  },
  {
    question: "Q: What input(s) need to be provided for the PA to be created?",
    answer: `A: Parent Application Number (Required), Original Specifications (Optional), Complete specifications with Amendments (Optional)`
  },
  {
    question: "Q: In which format should the original and amended specifications be uploaded?",
    answer: `A: DOC/DOCX format`
  },
  {
    question: "Q: What portions are recorded in the PA document?",
    answer: `A: Any amendments in cross-reference section, title, specification, and/or claims.`
  },
  {
    question: "Q: Does the current PA document cover amendments in Drawings?",
    answer: `A: No, the current version of the PA document DOES NOT cover amendments in the drawings. We would also suggest users to upload the specifications without drawings so that it does not affect the quality and accuracy of the JUNE generated PA document.`
  },
  {
    question: "Q: Is a manual review of June created PA document required?",
    answer: `A: Yes. Since the PA document is system identified amendments, there might be variations from the expected output. We suggest a quick manual review for a final draft to ensure accuracy.`
  },
  {
    question: "Q: Is a clean copy of the amended specifications also created?",
    answer: `A: No, currently a separate clean copy of the amended specifications is no part of the AppGen filing package.`
  },
  {
    question: "Q: Are the amendments in the specifications recorded in a separate copy from the PA document.",
    answer: `A: No. currently, all the amendments in the specifications are also recorded in the same PA document.`
  },
  {
    question: "Q: What does yellow and blue highlighting mean in the PA document?",
    answer: `A: It means that portion in the PA document requires specific manual review and needs to be updated by the user.`
  },
  {
    question: "Q: A cross reference section is already available in my original specification. Why did JUNE not amend it, but replace it with a new one completely?",
    answer: `A: JUNE utilizes a standard template to add the cross-reference section in the Preliminary Amendment.
  If the amened specification is uploaded AND it has a Cross reference section, then JUNE will compare that against the Cross-reference section of the Original specification and create a PA document with changes to the Original. However, in absence of the amended copy of the specification, JUNE will utilize the system standard template to update the cross-reference section in the created PA document.`
  },
];

const AppGenFAQ = () => {
  return (
    <Box>
      {faqData.map((item, index) => (
        <Accordion
          key={index}
          defaultExpanded={index === 0}
          sx={{
            backgroundColor: "#fff",
            "&.Mui-expanded": {
              margin: 0,
            },
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {typeof item.answer === "string" ? (
              <Typography sx={{ whiteSpace: "pre-line" }}>{item.answer}</Typography>
            ) : (
              item.answer
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default AppGenFAQ;
